/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Pagination, Typography, styled } from '@ethings/ui-toolkit';
import { PaginationItem } from '@mui/material';
import { FC, useMemo, useRef, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { scrollIntoView } from 'seamless-scroll-polyfill';
import { Icon } from '../../components';
import { FilterDrawerContent, FilterElement } from '../../components/filter';
import { useSmallScreen } from '../../hooks';
import { testid, trackEvent } from '../../util';
import { ListPageViewDesktop } from './view-desktop';
import { ListPageViewMobile } from './view-mobile';
import { Props } from './view.types';

const ZERO_WIDTH_SPACE = '​';

export const ListPageView: FC<Props> = (props): JSX.Element => {
  const smallScreen = useSmallScreen();
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    appliedFilters,
    dataExportConfig,
    dataExportOverrides,
    extraActions,
    fields,
    filterDrawerComponent,
    isLoading,
    matchCount,
    onFiltersChanged,
    pagination,
    removeAllFilters,
    removeFilter,
    setFilterOpen,
    setPagination,
    showDeletedLabel,
    showExtraActions,
    storageKey,
    subtitle,
    id,
    ...internal
  } = props;
  const { t } = useTranslation(['common']);
  const filtersLength = appliedFilters.length;

  const subtitleComponent = subtitle
    ? <Typography variant="subtitle2" color="GrayText" fontStyle="italic">{subtitle}</Typography>
    : <Typography variant="subtitle2" component="div">{ZERO_WIDTH_SPACE}</Typography>;

  const paginationComponent = useMemo(
    () => matchCount > pagination.limit
      ? <Pagination
        count={Math.ceil(matchCount / pagination.limit)}
        disabled={isLoading}
        onChange={(_, page) => {
          setPagination({ limit: pagination.limit, skip: pagination.limit * (page - 1) });
          scrollIntoView(scrollRef.current as Element, { behavior: 'smooth', block: 'start' });
          trackEvent('list', 'update_pagination', `${id}_list`, { 'results_type': 'geofence', 'page_number': page });
        }}
        renderItem={(item) => (
          <PaginationItem data-testid={testid`pagination-item-${item.type}${item.type === 'page' ? `-${item.page ?? ''}` : ''}`}
            {...item}
          />
        )}
        page={Math.floor(pagination.skip / pagination.limit) + 1}
        showFirstButton={!smallScreen}
        showLastButton={!smallScreen}
        size={smallScreen ? 'small' : 'large'}
      />
      : undefined,
    [isLoading, matchCount, pagination, smallScreen],
  );

  const defaultFilterDrawerComponent = (
    <FilterDrawerContent
      fields={fields}
      filters={appliedFilters}
      onCloseClicked={() => setFilterOpen(false)}
      onFiltersChanged={onFiltersChanged}
      showDeletedLabel={showDeletedLabel}
      storageKey={storageKey}
    />
  );

  const ClearFiltersButton = styled(Button)(({ theme }) => ({
    marginRight: theme.spacing(1),
  }));

  const clearAllFiltersComponent = (
    <ClearFiltersButton
      data-testid="clear-filters-button"
      onClick={removeAllFilters}
      startIcon={<Icon data-testid="clear-icon" icon="CloseIcon" />}
      variant="text"
    >
      {t('common:component.filter.actions.clear')}
    </ClearFiltersButton>
  );

  const appliedFiltersComponents = useMemo(
    () => !smallScreen && appliedFilters.length
      ? <>
        {appliedFilters.map((filter) =>
          <FilterElement
            key={filter.id}
            filter={filter}
            onRemoveClicked={() => removeFilter(filter)}
            sx={{ my: .5 }}
          />,
        )}
      </>
      : undefined,
    [appliedFilters, smallScreen],
  );

  const commonViewProps = {
    ...internal,
    id,
    appliedFilters,
    appliedFiltersComponents,
    clearAllFiltersComponent,
    dataExportConfig,
    dataExportOverrides,
    extraActions,
    filterDrawerComponent: filterDrawerComponent ?? defaultFilterDrawerComponent,
    filtersLength,
    paginationComponent,
    removeAllFilters,
    scrollRef,
    setFilterOpen,
    showExtraActions,
    subtitleComponent,
  };

  return smallScreen ? (
    <ListPageViewMobile {...commonViewProps} />
  ) : (
    <ListPageViewDesktop {...commonViewProps} />
  );
};
